import { TZDateMini } from '@date-fns/tz';
import { endOfDay } from 'date-fns/endOfDay';
import { formatDistanceToNow } from 'date-fns/formatDistanceToNow';
import { sub } from 'date-fns/sub';

import { RELATIVE_DATE_PRESET_OPTIONS } from '@models/date.const';
import type { RelativeDatePreset } from '@models/date.models';
import type { DateRangeValue } from '@components/date-range';

/**
 * Type guard for the custom-range tuple variant of `DateRangeValue`. Wraps
 * `Array.isArray` in a predicate so TypeScript narrows the readonly tuple —
 * the built-in `Array.isArray` narrowing drops the `readonly` marker and
 * won't discriminate against the preset-string variant in the union.
 */
export const isCustomDateRange = (
  range: DateRangeValue,
): range is readonly [string, string | null] => Array.isArray(range);

/**
 * Returns an offset date based on the given relative preset value. Optionally
 * accepts a date to offset from, otherwise defaults to now.
 */
export const getRelativeDate = (preset: RelativeDatePreset, date?: Date) => {
  const utc = date ? new TZDateMini(date, 'UTC') : TZDateMini.tz('UTC');

  return sub(utc, { days: RELATIVE_DATE_PRESET_OPTIONS[preset].days });
};

/**
 * Returns a start and end date range based on the given relative preset value.
 * Optionally accepts an end date, otherwise defaults to now.
 */
export const getRelativeDateRange = (
  preset: RelativeDatePreset,
  endDate?: Date,
) => {
  const end = endDate ? new TZDateMini(endDate, 'UTC') : TZDateMini.tz('UTC');
  const start = getRelativeDate(preset, end);

  return [start, end] as const;
};

/**
 * Resolves a `DateRangeValue` into a concrete `[start, end]` tuple of UTC
 * dates.
 *
 * - **Custom-range tuple** (`[startISO, endISO]`): each side is parsed as a
 *   `TZDateMini` in UTC; missing or unparseable entries become `undefined`,
 *   which the destructure backfills to `now` for `start` and `null` for `end`.
 * - **Relative preset** (e.g. `'7d'`): delegated to `getRelativeDateRange`,
 *   which produces both bounds.
 *
 * Returned as a readonly tuple so callers can destructure positionally.
 */
export const parseDateRange = (range: DateRangeValue) => {
  const [start = TZDateMini.tz('UTC'), end = null] = isCustomDateRange(range)
    ? range.map(maybeDate => {
        const parsedDate = new TZDateMini(maybeDate || '', 'UTC');

        if (!maybeDate || Number.isNaN(parsedDate.valueOf())) return undefined;

        return parsedDate;
      })
    : getRelativeDateRange(range);

  return [start, end] as const;
};

/**
 * Returns the relative-date string for a date (e.g. "2 days ago"), optionally capitalized.
 */
export const formatRelativeDate = (
  date: string | number,
  { capitalize = false }: { capitalize?: boolean } = {},
): string => {
  const dateValue =
    typeof date === 'number' ? new Date(date * 1000) : new Date(date);
  const localDate = new TZDateMini(new TZDateMini(dateValue, 'UTC'));
  const raw = formatDistanceToNow(localDate, { addSuffix: true });
  return capitalize ? raw.charAt(0).toUpperCase() + raw.slice(1) : raw;
};

/**
 * Expands a `DateRangeValue` into a `[after, before]` tuple of ISO-formatted
 * bounds for server-side range filters, mirroring {@link parseDateRange}'s
 * tuple shape. Presets produce only an `after` bound; an `undefined` range
 * produces no bounds.
 *
 * Custom-range values are the local-midnight ISO emitted by `formatISO(Date)`
 * inside the picker, so the `after` bound passes straight through. Only the
 * `before` bound needs `endOfDay` to extend the end date through the rest of
 * the user's wall-clock day.
 */
export const toDateRangeBounds = (
  range?: DateRangeValue,
): readonly [string | undefined, string | undefined] => {
  if (!range) return [undefined, undefined];

  if (isCustomDateRange(range)) {
    const [start, end] = range;

    return [
      start ? new Date(start).toISOString() : undefined,
      end ? endOfDay(new Date(end)).toISOString() : undefined,
    ];
  }

  return [getRelativeDate(range).toISOString(), undefined];
};
